import { getLoyaltySDK } from "../../api/loyaltySdk";
import { isLiveDate } from "./eventUtils";
import {
  getLoyaltyConfig,
  hasBenefitAccess,
  getBenefitTypeConfig,
} from "./loyaltySdkUtils";
import { getAdditionalContextFromProduct } from "./contextUtils";
import {
  APP_MEMBERS_PRE_SALE,
  APP_MEMBERS_SALE_LIVE,
  MEMBERS_DAY_BENEFIT_TYPE,
  MEMBERS_DAY_SUBTYPE,
  PRE_SALE_SUBTYPE,
} from "../../constants";

const emptyMembersSaleContext = {
  membersDayContext: null,
  startDate: null,
  endDate: null,
  membersSaleLive: false,
  analyticsProps: {},
};

const hasRequiredContext = (membersDayContext, membersDayPreSaleContext) =>
  !!membersDayContext || !!membersDayPreSaleContext;

export const getMembersDayContext = async (product) => {
  const membersDayContext = getAdditionalContextFromProduct(
    product,
    APP_MEMBERS_SALE_LIVE
  );
  const membersDayPreSaleContext = getAdditionalContextFromProduct(
    product,
    APP_MEMBERS_PRE_SALE
  );

  if (!hasRequiredContext(membersDayContext, membersDayPreSaleContext)) {
    return emptyMembersSaleContext;
  }

  const loyaltySdk = await getLoyaltySDK();

  const canViewMembersDayBenefit = await hasBenefitAccess(
    loyaltySdk,
    MEMBERS_DAY_BENEFIT_TYPE
  );

  if (!canViewMembersDayBenefit) {
    return emptyMembersSaleContext;
  }

  const loyaltyConfig = await getLoyaltyConfig(loyaltySdk);
  const membersOnlySaleConfig = getBenefitTypeConfig(
    loyaltyConfig,
    MEMBERS_DAY_BENEFIT_TYPE
  );

  if (!membersOnlySaleConfig) {
    return emptyMembersSaleContext;
  }

  const membersDayEventLive = isEventLive(
    APP_MEMBERS_SALE_LIVE,
    membersOnlySaleConfig?.eventSchedule?.events
  );
  const preSaleEventLive = isEventLive(
    APP_MEMBERS_PRE_SALE,
    membersOnlySaleConfig?.eventSchedule?.events
  );

  const targetContext = getTargetContext(
    membersDayEventLive,
    preSaleEventLive,
    membersDayContext,
    membersDayPreSaleContext
  );

  if (!targetContext) {
    return emptyMembersSaleContext;
  }

  const { startDate, endDate } = getMembersDayStartAndEndDate(
    membersOnlySaleConfig
  );

  const tierDetails = await getTierDetailsByTierName(loyaltyConfig, loyaltySdk);

  return {
    membersDayContext: targetContext,
    startDate,
    endDate,
    membersSaleLive: membersDayEventLive,
    analyticsProps: tierDetails,
  };
};

const getMembersDayEventBySubType = (events, contextName) => {
  const subType = getLoyaltyConfigSubType(contextName);
  return events?.find((item) => {
    return item.subType === subType;
  });
};

const getLoyaltyConfigSubType = (contextName) =>
  contextName === APP_MEMBERS_SALE_LIVE
    ? MEMBERS_DAY_SUBTYPE
    : PRE_SALE_SUBTYPE;

const getMembersDayStartAndEndDate = (membersDayConfig) => {
  const membersDayEvent = getMembersDayEventBySubType(
    membersDayConfig?.eventSchedule?.events,
    APP_MEMBERS_SALE_LIVE
  );

  return {
    startDate: membersDayEvent?.startDate,
    endDate: membersDayEvent?.endDate,
  };
};

const isEventLive = (contextName, events) => {
  const event = getMembersDayEventBySubType(events, contextName);
  return isLiveDate(event?.startDate, event?.endDate);
};

const getTargetContext = (
  membersSaleLive,
  preSaleEventLive,
  membersDayContext,
  membersDayPreSaleContext
) => {
  if (membersSaleLive && membersDayContext) {
    return membersDayContext;
  }
  if (preSaleEventLive && membersDayPreSaleContext) {
    return membersDayPreSaleContext;
  }
  return null;
};

export const getTierDetailsByTierName = async (loyaltyConfig, loyaltySdk) => {
  try {
    const currentTier = await loyaltySdk.getCustomerTierId();
    const tier = loyaltyConfig.tiers.find((tier) => tier.id === currentTier);
    return {
      tierName: tier?.name?.toLowerCase() ?? null,
      tierNumber: tier?.tierNumber ?? null,
    };
  } catch (error) {
    return {
      tierName: null,
      tierNumber: null,
    };
  }
};
